import React, { useState } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Input, 
  Modal, 
  Form, 
  message,
  Tag,
  Tooltip,
  Row,
  Col
} from 'antd';
import { 
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  ExportOutlined,
  ImportOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { translationsService, type Language } from '../../api/translations';

const TranslationsList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingLanguage, setEditingLanguage] = useState<Language | null>(null);

  // Fetch languages data
  const { data: languagesData, isLoading, error } = useQuery({
    queryKey: ['languages', searchText],
    queryFn: async () => {
      const result = await translationsService.getLanguages({ limit: 50 });
      console.log('API Response:', result);
      return result;
    },
    retry: 1,
  });

  // Delete language mutation
  const deleteLanguageMutation = useMutation({
    mutationFn: (languageId: string) => translationsService.deleteLanguage(languageId),
    onSuccess: () => {
      message.success('Язык удален');
      queryClient.invalidateQueries({ queryKey: ['languages'] });
    },
    onError: (error) => {
      console.error('Delete language error:', error);
      message.error('Ошибка при удалении языка');
    },
  });

  // Create/Update language mutation
  const saveLanguageMutation = useMutation({
    mutationFn: (data: { id?: string; code: string; name: string; nativeName: string }) => {
      if (data.id) {
        return translationsService.updateLanguage(data.id, {
          code: data.code,
          name: data.name,
          nativeName: data.nativeName,
        });
      } else {
        return translationsService.createLanguage({
          code: data.code,
          name: data.name,
          nativeName: data.nativeName,
        });
      }
    },
    onSuccess: () => {
      message.success(editingLanguage ? 'Язык обновлен' : 'Язык создан');
      queryClient.invalidateQueries({ queryKey: ['languages'] });
      setIsModalVisible(false);
      setEditingLanguage(null);
      form.resetFields();
    },
    onError: (error) => {
      console.error('Save language error:', error);
      message.error('Ошибка при сохранении языка');
    },
  });

  const columns = [
    {
      title: 'Код',
      dataIndex: 'code',
      key: 'code',
      width: 80,
      render: (code: string) => (
        <Tag color="blue" style={{ fontWeight: 'bold' }}>
          {code.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      width: 150,
    },
    {
      title: 'Родное название',
      dataIndex: 'nativeName',
      key: 'nativeName',
      width: 150,
    },
    {
      title: 'Статус',
      key: 'status',
      width: 120,
      render: (record: Language) => (
        <Space>
          {record.isDefault && <Tag color="gold">По умолчанию</Tag>}
          <Tag color={record.isActive ? 'green' : 'red'}>
            {record.isActive ? 'Активный' : 'Неактивный'}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Переводов',
      key: 'translations',
      width: 100,
      render: (record: Language) => (
        <Tag color="purple">{record._count?.translations || 0}</Tag>
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 200,
      render: (record: Language) => (
        <Space>
          <Tooltip title="Управление переводами">
            <Button 
              type="primary" 
              size="small" 
              icon={<GlobalOutlined />}
              onClick={() => navigate(`/translations/languages/${record.id}`)}
            >
              Переводы
            </Button>
          </Tooltip>
          <Tooltip title="Редактировать язык">
            <Button 
              size="small" 
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          {!record.isDefault && (
            <Tooltip title="Удалить язык">
              <Button 
                size="small" 
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const handleEdit = (language: Language) => {
    setEditingLanguage(language);
    form.setFieldsValue(language);
    setIsModalVisible(true);
  };

  const handleDelete = (languageId: string) => {
    Modal.confirm({
      title: 'Удалить язык?',
      content: 'Это действие нельзя отменить. Все переводы для этого языка будут удалены.',
      onOk: () => {
        deleteLanguageMutation.mutate(languageId);
      },
    });
  };

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      saveLanguageMutation.mutate({
        id: editingLanguage?.id,
        code: values.code,
        name: values.name,
        nativeName: values.nativeName,
      });
    });
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingLanguage(null);
    form.resetFields();
  };

  const filteredData = (languagesData?.items || []).filter((item: Language) =>
    item.name.toLowerCase().includes(searchText.toLowerCase()) ||
    item.nativeName.toLowerCase().includes(searchText.toLowerCase()) ||
    item.code.toLowerCase().includes(searchText.toLowerCase())
  );

  if (isLoading) {
    return <div>Загрузка...</div>;
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Ошибка загрузки языков: {(error as Error).message}</p>
        <Button onClick={() => window.location.reload()}>Перезагрузить</Button>
      </div>
    );
  }

  return (
    <div className="translations-list">
      <Card 
        title={
          <Space>
            <GlobalOutlined />
            <span>Управление языками</span>
          </Space>
        }
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => setIsModalVisible(true)}
          >
            Добавить язык
          </Button>
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Input
              placeholder="Поиск по названию или коду..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col span={16} style={{ textAlign: 'right' }}>
            <Space>
              <Button icon={<ImportOutlined />}>
                Импорт языков
              </Button>
              <Button icon={<ExportOutlined />}>
                Экспорт языков
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} из ${total} языков`,
          }}
        />
      </Card>

      <Modal
        title={editingLanguage ? 'Редактировать язык' : 'Добавить язык'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            isActive: true,
            isDefault: false,
          }}
        >
          <Form.Item
            name="code"
            label="Код языка"
            rules={[
              { required: true, message: 'Введите код языка' },
              { len: 2, message: 'Код должен содержать 2 символа' },
            ]}
          >
            <Input 
              placeholder="en, ru, uz..." 
              maxLength={2}
              style={{ textTransform: 'lowercase' }}
            />
          </Form.Item>

          <Form.Item
            name="name"
            label="Название на английском"
            rules={[{ required: true, message: 'Введите название языка' }]}
          >
            <Input placeholder="English, Russian, Uzbek..." />
          </Form.Item>

          <Form.Item
            name="nativeName"
            label="Родное название"
            rules={[{ required: true, message: 'Введите родное название языка' }]}
          >
            <Input placeholder="English, Русский, O'zbekcha..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TranslationsList;
